import Account from './Account'
import Settings from './Settings'
import HelpPage from './HelpPage'
import LeftNav from './components/LeftNav'
import UploadXmlPage from './UploadXmlPage'
import { useState, useEffect } from 'react'
import { subscribe, unsubscribe } from './events'
import ReportsList from './components/ReportsList'
import GettingStartedPage from './GettingStartedPage'
import EpimsLogoOnly from './components/EpimsLogoOnly'
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro'


function HomePage(props) {

  // on component mount, fetch the latest user settings (this is mostly to get the email template text) and then subscribe to messages indicating that the settings have changed
  // on one of those messages, fetch again
  // on unmount, unsubscribe
  useEffect(() => {
    props.fetchUserSettings();
    const handleSettingsUpdateMessage = (body) => {
      props.fetchUserSettings();
    }
    subscribe('settingsUpdate', handleSettingsUpdateMessage);
    return () => {
      unsubscribe('settingsUpdate', handleSettingsUpdateMessage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [messagesLoading, setMessagesLoading] = useState(true)
  const [newTerms, setNewTerms] = useState([])
  const [newTermsTime, setNewTermsTime] = useState(0)
  const [newMessage, setNewMessage] = useState({})
  const [showMessageType, setShowMessageType] = useState('')
  const [messagesCleared, setMessagesCleared] = useState(false)

  const checkMessages = () => {
    setMessagesLoading(true)
    setMessagesCleared(false)
    const url = process.env.REACT_APP_API_END_POINT + 'checkmessages';
    fetch(url, {
      headers: {
        'Accept': 'application/json',
      },
      method: "GET",
      credentials: "include",
    })
    .then((resp) => resp.json())
    .then((data) => {
      if (data['success']) {
        setMessagesLoading(false)
        if (data['type'] === 'NONE') {
            setMessagesCleared(true)
        } else if (data['type'] === 'TERMS') {
            setNewTerms(data['terms'])
            setNewTermsTime(data['updated_at'])
            setShowMessageType('terms')
        } else if (data['type'] === 'MESSAGE') {
            setNewMessage(data['message'])
            setShowMessageType('message')
        } else if (data['type'] === 'AUTO') {
            setNewMessage(data['message'])
            setShowMessageType('auto')
        }
      }
    });
  }

  const acceptTerms = () => {
    setMessagesLoading(true)
    const url = process.env.REACT_APP_API_END_POINT + 'checkmessages';
    fetch(url, {
      headers: {
        'Accept': 'application/json',
      },
      method: "POST",
      credentials: "include",
      body: JSON.stringify({
        type: 'TERMS',
        acceptTerms: true,
        acceptedTo: newTermsTime,
      })
    })
    .then((resp) => resp.json())
    .then((data) => {
      if (data['success']) {
        checkMessages()
      }
    });
  }

  const answerMessage = (e, response) => {
    setMessagesLoading(true)
    const url = process.env.REACT_APP_API_END_POINT + 'checkmessages';
    fetch(url, {
      headers: {
        'Accept': 'application/json',
      },
      method: "POST",
      credentials: "include",
      body: JSON.stringify({
        type: showMessageType.toUpperCase(),
        id: newMessage.id,
        response
      })
    })
    .then((resp) => resp.json())
    .then((data) => {
      if (data['success']) {
        checkMessages()
      }
    });
  }

  // on component mount, check if there are any messages for us
  useEffect(() => {
    checkMessages()
  }, [])

  const showMessage = () => {
    return (
      <>
        <div className="contentsAreaHeader">
          <div className="areaTitle">A message from epIMS</div>
          <div className="areaExplainer">Please read the message before continuing</div>
        </div>
        <div className="contents">
          <div style={{display: "flex", flexDirection: "column", maxWidth: "650px", marginInline: "auto", marginBlock: "auto", borderRadius: "30px", border: "1px solid black", padding: "6px"}}>
              <div style={{marginInline: "auto"}}>
                  <EpimsLogoOnly xsmall />
              </div>
              {showMessageType === 'terms' && <>
                  <p style={{fontWeight: "bold", fontSize: "30px"}}>We have updated our terms and conditions since your last visit</p>
                  <p style={{fontWeight: "bold", fontSize: "20px"}}>Please accept the updated terms below</p>
                  {newTerms.map(term => {return <>
                      <p style={{fontWeight: "bold"}}>{term.title}</p>
                      <p dangerouslySetInnerHTML={{ __html: term.message_html}} />
                  </>})}
                  <div style={{marginInline: "auto"}}>
                      <button id="acceptTermsButton" onClick={acceptTerms} className="btn btn-primary">Accept all updated terms</button>&nbsp;<button id="declineTermsButton" onClick={props.doLogout} className="btn btn-secondary">Decline updates and log out</button>
                  </div>
              </>}
              {(showMessageType === 'message' || showMessageType === 'auto') && <>
                  <p style={{fontWeight: "bold", textAlign: "center", marginTop: "20px"}}>{newMessage.title}</p>
                  <p dangerouslySetInnerHTML={{ __html: newMessage.message_html}} />
                  <div style={{marginInline: "auto", textAlign: "center"}}>
                    {newMessage.options.map((option, idx) => {
                      if (option.type === 'button') {
                        return <button key={"question-button-" + idx} style={{margin: "6px"}} onClick={(e) => answerMessage(e, option.value)} className={idx === 0 ? "btn btn-primary" : "btn btn-secondary"}>{option.value}</button>
                      }
                      return <></>
                    })}
                  </div>
              </>}
          </div>
        </div>
      </>
    )
  }

  const mainComponent = () => {
    
    // if we are loading and messages still, then return a spinny
    if (messagesLoading) return <div style={{width: "100%", height: "100%", display: "flex", flexDirection: "column"}}><FontAwesomeIcon className="spinner-slow" icon={solid("spinner")} style={{marginInline: "auto", marginBlock: "auto", color: "#D0D0D0", height: "150px", width: "150px" }} /></div>

    // if there are uncleared messages, return them
    if (!messagesCleared) return showMessage()

    // show the regular page
    if (props.activeTab === 'myaccount') return <Account userRole={props.userRole} />
    if (props.activeTab === 'settings') return <Settings />
    if (props.activeTab === 'main') return <ReportsList key="regular" userSettings={props.userSettings} mode="preliminary" deleted={false} userRole={props.userRole} />
    if (props.activeTab === 'deleted') return <ReportsList key="deleted" userSettings={props.userSettings} mode="preliminary" deleted={true} userRole={props.userRole} />
    if (props.activeTab === 'new') return (
      <UploadXmlPage {...{
        userAccreditationScheme: props.userAccreditationScheme,
        premiumCreditsRemaining: props.premiumCreditsRemaining,
        userRole: props.userRole
      }} />
    )
    if (props.activeTab === 'help') return <HelpPage />
    if (props.activeTab === 'home') return <GettingStartedPage premiumCreditsRemaining={props.premiumCreditsRemaining} />
  }

  return (
    <div className="loggedInContainer">
      <div className="loggedInHeader"></div>
      <div className="loggedInMainArea">
        <LeftNav {...{
          userRealName: props.userRealName,
          activeTab: props.activeTab,
          doLogout: props.doLogout,
          loginCount: props.loginCount,
          sectionsVisited: props.sectionsVisited,
          markSectionVisited: props.markSectionVisited
        }} />
        <div className="contentArea">
          { mainComponent() }
        </div>
      </div>
    </div>
  );

}

export default HomePage;
